import { useEffect, useRef, useState } from "react";
import { A } from "./assets";
import { Basis, Icon } from "./core";

/* ====================================================================
   Chef Buddy: the guest conversation.
   Two rules for anyone editing these scripts:
   1. Chef Buddy never states a figure a guest could check and we could not defend.
   2. Margin context is for the operator. It never appears in a guest conversation.
   ==================================================================== */
type Msg =
  | { from: "guest" | "buddy"; text: string; rtl?: boolean }
  | { from: "dish"; name: string; note: string; chips: { t: string; kind?: "ok" | "warn" }[] }
  | { from: "note"; text: string };
export type Scenario = { id: string; tab: string; msgs: Msg[] };

export const GUEST_SCENARIOS: Scenario[] = [
  {
    id: "allergy", tab: "An allergy",
    msgs: [
      { from: "guest", text: "I have a tree-nut allergy. What is safe for me tonight?" },
      { from: "buddy", text: "Eleven dishes are cleared for tree nuts on the kitchen's allergen matrix. The lightest is the sea bream." },
      { from: "dish", name: "Sea bream, fennel, citrus", note: "Line-caught, Sicilian olive oil", chips: [{ t: "Tree-nut clear", kind: "ok" }, { t: "Gluten-free", kind: "ok" }] },
      { from: "buddy", text: "The pistachio cake is not cleared, so I will keep it out of anything I suggest. Shall I let your server know about the allergy?" },
      { from: "note", text: "Allergen answers are read from a matrix the kitchen has signed. If a dish is not on it, Chef Buddy says so and calls a person." },
    ],
  },
  {
    id: "pairing", tab: "A pairing",
    msgs: [
      { from: "guest", text: "What would you drink with the lamb?" },
      { from: "buddy", text: "The kitchen braises the shoulder for six hours with dried lime, so it wants something with spine. The northern Rhône Syrah is open by the glass tonight." },
      { from: "guest", text: "And without alcohol?" },
      { from: "buddy", text: "The pomegranate and black tea cooler. It has the same tannin grip, and the bar makes it to order." },
    ],
  },
  {
    id: "language", tab: "In Arabic",
    msgs: [
      { from: "guest", text: "ما هو أخف طبق نباتي الليلة؟", rtl: true },
      { from: "buddy", text: "أخف طبق نباتي الليلة هو سلطة الشمندر الدافئة مع الحمص، وهو نباتي بالكامل.", rtl: true },
      { from: "dish", name: "سلطة الشمندر الدافئة", note: "Warm beetroot, chickpea, labneh", chips: [{ t: "Vegetarian", kind: "ok" }, { t: "Contains dairy", kind: "warn" }] },
      { from: "note", text: "Guests ask by voice or text in more than 60 languages. The kitchen ticket still prints in the kitchen's language." },
    ],
  },
  {
    id: "order", tab: "An order",
    msgs: [
      { from: "guest", text: "The sea bream for me, and a sparkling water. My daughter will have the tagliolini, no chilli." },
      { from: "buddy", text: "Done. One sea bream, one spring tagliolini without chilli, one sparkling water, for Table 12." },
      { from: "buddy", text: "Your order is with the kitchen and your server can see it. Would you like the dessert menu later, or with the mains?" },
    ],
  },
];

export function BuddyChat({ scenarios = GUEST_SCENARIOS, place = "Table 12" }: { scenarios?: Scenario[]; place?: string }) {
  const [idx, setIdx] = useState(0);
  const [shown, setShown] = useState(99); // complete at rest; a tab change replays it
  const timer = useRef<number | undefined>(undefined);
  const sc = scenarios[idx];
  useEffect(() => () => window.clearTimeout(timer.current), []);
  function play(i: number) {
    window.clearTimeout(timer.current);
    setIdx(i);
    const reduce = typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setShown(99); return; }
    setShown(1);
    const step = (n: number) => { timer.current = window.setTimeout(() => { setShown(n); if (n < scenarios[i].msgs.length) step(n + 1); }, 850); };
    step(2);
  }
  return (
    <div className="tk-demo">
      <div className="tk-tabs" role="group" aria-label="What the guest asks">
        {scenarios.map((s, i) => <button key={s.id} className="tk-tab" aria-pressed={i === idx} onClick={() => play(i)}>{s.tab}</button>)}
      </div>
      <div className="tk-phone">
        <div className="tk-phone__screen">
          <div className="tk-phone__bar">
            <span className="tk-avatar"><img src={A.chefBuddy} alt="" /></span>
            <div><div className="tk-phone__title">Chef Buddy</div><div className="tk-phone__sub">{place}</div></div>
            <span className="tk-live">Live</span>
          </div>
          <div className="tk-msgs" aria-live="polite">
            {sc.msgs.slice(0, shown).map((m, i) => {
              if (m.from === "dish") return (
                <div key={i} className="tk-dish tk-msg--in">
                  <img src={A.photoDish} alt="" />
                  <div><b dir="auto">{m.name}</b><span style={{ fontSize: "0.8rem", color: "var(--on-bone-mute)" }}>{m.note}</span>
                    <span className="tk-chips">{m.chips.map((c) => <span key={c.t} className={"tk-chip" + (c.kind ? ` tk-chip--${c.kind}` : "")}>{c.t}</span>)}</span></div>
                </div>
              );
              if (m.from === "note") return <div key={i} className="tk-msg tk-msg--note tk-msg--in">{m.text}</div>;
              return <div key={i} className={`tk-msg tk-msg--${m.from} tk-msg--in`} dir={m.rtl ? "rtl" : undefined} lang={m.rtl ? "ar" : undefined}>{m.text}</div>;
            })}
          </div>
          <div className="tk-phone__input">Ask about any dish<Icon name="mic" /></div>
        </div>
      </div>
    </div>
  );
}

/* ====================================================================
   Chef Brain: menu engineering matrix (Kasavana and Smith, 1982).
   Example data. Quadrant colours are fixed and always paired with a label.
   ==================================================================== */
type Q = "star" | "plow" | "puzzle" | "dog";
const QUADS: Record<Q, { name: string; verb: string; color: string; line: string; move: string }> = {
  star: { name: "Stars", verb: "Protect", color: "var(--q-star)", line: "High margin, high volume. These pay for the room.", move: "Never discount them, keep them on the first fold, and re-cost them the moment a supplier price moves." },
  plow: { name: "Plowhorses", verb: "Reprice", color: "var(--q-plow)", line: "Everyone orders them. Nobody profits from them.", move: "Re-cost the recipe, adjust portion or price, and let the elasticity read show how far you can go before covers move." },
  puzzle: { name: "Puzzles", verb: "Reposition", color: "var(--q-puzzle)", line: "Strong margin, weak sales.", move: "Rename, redescribe, move up the page, brief the floor. The quickest margin gain on most menus is here." },
  dog: { name: "Dogs", verb: "Remove", color: "var(--q-dog)", line: "Low on both axes.", move: "Cut them or rebuild them. Each one costs prep time, stock lines and a share of the guest's attention." },
};
const DISHES: { n: string; x: number; y: number; q: Q }[] = [
  { n: "Sea bream", x: 78, y: 80, q: "star" }, { n: "Lamb shoulder", x: 66, y: 68, q: "star" }, { n: "Burrata", x: 88, y: 62, q: "star" },
  { n: "Wagyu burger", x: 92, y: 30, q: "plow" }, { n: "Margherita", x: 74, y: 22, q: "plow" }, { n: "Caesar salad", x: 62, y: 36, q: "plow" },
  { n: "Lobster linguine", x: 24, y: 86, q: "puzzle" }, { n: "Duck breast", x: 30, y: 72, q: "puzzle" }, { n: "Truffle risotto", x: 14, y: 64, q: "puzzle" },
  { n: "Club sandwich", x: 30, y: 26, q: "dog" }, { n: "Onion soup", x: 16, y: 14, q: "dog" },
];
export function Matrix({ active, compact }: { active?: Q; compact?: boolean }) {
  const W = 560, H = compact ? 330 : 400, L = 46, R = 14, T = 14, B = 40;
  const px = (v: number) => L + (v / 100) * (W - L - R);
  const py = (v: number) => H - B - (v / 100) * (H - T - B);
  return (
    <svg className="tk-matrix" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Menu engineering matrix. Dishes plotted by popularity and contribution margin into four groups: Stars, Plowhorses, Puzzles and Dogs. Example data.">
      <rect x={L} y={T} width={W - L - R} height={H - T - B} fill="#FBF9F6" stroke="#D9D0C4" />
      <line x1={px(50)} y1={T} x2={px(50)} y2={H - B} stroke="#B9AFA4" strokeDasharray="4 4" />
      <line x1={L} y1={py(50)} x2={W - R} y2={py(50)} stroke="#B9AFA4" strokeDasharray="4 4" />
      {([["puzzle", 2, 96, "start"], ["star", 98, 96, "end"], ["dog", 2, 3, "start"], ["plow", 98, 3, "end"]] as [Q, number, number, "start" | "end"][]).map(([q, x, y, a]) => (
        <text key={q} x={px(x)} y={py(y) + (y > 50 ? 8 : -2)} textAnchor={a} fontSize="11" fontFamily="var(--f-mono)" letterSpacing="1" fill="#5C5349">{QUADS[q].name.toUpperCase()}</text>
      ))}
      {DISHES.map((d) => {
        const on = !active || active === d.q;
        const right = d.x > 80;
        return (
          <g key={d.n} opacity={on ? 1 : 0.22}>
            <circle cx={px(d.x)} cy={py(d.y)} r={on && active ? 8 : 6.5} fill={QUADS[d.q].color} stroke="#fff" strokeWidth="1.5" />
            {!compact && <text x={px(d.x) + (right ? -11 : 11)} y={py(d.y) + 4} textAnchor={right ? "end" : "start"} fontSize="12" fill="#17130F">{d.n}</text>}
          </g>
        );
      })}
      <text x={px(50)} y={H - 10} textAnchor="middle" fontSize="12" fill="#5C5349">Popularity (share of covers) →</text>
      <text x={14} y={py(50)} textAnchor="middle" fontSize="12" fill="#5C5349" transform={`rotate(-90 14 ${py(50)})`}>Contribution margin →</text>
    </svg>
  );
}
export function MatrixExplorer() {
  const [q, setQ] = useState<Q>("puzzle");
  return (
    <div className="tk-stack">
      <div className="tk-board">
        <div className="tk-board__bar"><img src={A.kmarkInk} alt="" /><span>Chef Brain · menu matrix</span><span>Dinner · last 28 days</span></div>
        <div className="tk-board__body"><Matrix active={q} /></div>
      </div>
      <div className="tk-quads" role="group" aria-label="Menu groups">
        {(Object.keys(QUADS) as Q[]).map((k) => (
          <button key={k} className="tk-quad" aria-pressed={q === k} onClick={() => setQ(k)}>
            <i className="tk-quad__dot" style={{ background: QUADS[k].color }} />
            <b>{QUADS[k].name}</b><span>{QUADS[k].verb}</span>
          </button>
        ))}
      </div>
      <p className="tk-body" style={{ maxWidth: "none" }}><b style={{ color: "var(--ink)" }}>{QUADS[q].line}</b> {QUADS[q].move}</p>
      <p className="tk-fine"><Basis kind="Illustrative" /> &nbsp;Example dishes. Your matrix is built from your own point-of-sale data.</p>
    </div>
  );
}

/* Repricing simulation. A deliberately simple constant-elasticity example. */
export function RepriceSim() {
  const [pct, setPct] = useState(6);
  const base = { price: 95, covers: 420, cost: 38, e: -0.9 };
  const price = Math.round(base.price * (1 + pct / 100));
  const covers = Math.round(base.covers * Math.pow(price / base.price, base.e));
  const rev = price * covers, cm = (price - base.cost) * covers;
  const rev0 = base.price * base.covers, cm0 = (base.price - base.cost) * base.covers;
  const d = (a: number, b: number) => { const v = ((a - b) / b) * 100; return (v >= 0 ? "+" : "−") + Math.abs(v).toFixed(1) + "%"; };
  const fmt = (n: number) => n.toLocaleString("en-US");
  return (
    <div className="tk-board">
      <div className="tk-board__bar"><img src={A.kmarkInk} alt="" /><span>Chef Brain · repricing simulation</span><span>Wagyu burger</span></div>
      <div className="tk-board__body tk-sim">
        <label htmlFor="tk-sim-range" style={{ display: "grid", gap: 8 }}>
          <span style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <span>Move the price</span>
            <b style={{ fontFamily: "var(--f-mono)", fontWeight: 500 }}>AED {base.price} → AED {price} ({pct >= 0 ? "+" : "−"}{Math.abs(pct)}%)</b>
          </span>
          <input id="tk-sim-range" type="range" min={-10} max={20} step={1} value={pct} onChange={(e) => setPct(Number(e.target.value))} />
        </label>
        <div className="tk-sim__out">
          <div className="tk-kpi"><span>Weekly covers</span><b>{fmt(covers)}</b><i className={covers < base.covers ? "dn" : ""}>{d(covers, base.covers)}</i></div>
          <div className="tk-kpi"><span>Weekly revenue, AED</span><b>{fmt(rev)}</b><i className={rev < rev0 ? "dn" : ""}>{d(rev, rev0)}</i></div>
          <div className="tk-kpi"><span>Contribution, AED</span><b>{fmt(cm)}</b><i className={cm < cm0 ? "dn" : ""}>{d(cm, cm0)}</i></div>
        </div>
        <p className="tk-fine"><Basis kind="Illustrative" /> &nbsp;A simple model with one assumed elasticity. In Chef Brain the curve is fitted per dish from your own transactions, and it tells you when the history is too short to trust.</p>
      </div>
    </div>
  );
}

/* Chef Brain Chat: a plain-English question, a computed answer. */
const BRAIN_QS = [
  {
    q: "Show me how the lobster dish did last week, and compare it with the same week last year.",
    bars: [{ l: "Covers", a: 86, b: 71 }, { l: "Contribution, AED 00s", a: 95, b: 71 }],
    a: <>Lobster linguine sold <b>86 covers</b> last week against <b>71</b> in the same week last year. Contribution rose faster than covers because the recipe was re-costed in March. It is still a Puzzle: strong margin, low share of covers.</>,
    plan: `{ "measure": ["covers", "contribution_margin"],\n  "filter": { "item": "Lobster linguine" },\n  "compare": ["last_week", "same_week_last_year"] }`,
  },
  {
    q: "Which dishes lost margin this month, and why?",
    bars: [{ l: "Wagyu burger, margin %", a: 54, b: 60 }, { l: "Duck breast, margin %", a: 61, b: 66 }],
    a: <>Two dishes moved. <b>Wagyu burger</b> lost six points after the beef supplier's price rise on the 4th. <b>Duck breast</b> lost five points on a larger portion since the menu change. Neither price has moved.</>,
    plan: `{ "measure": ["contribution_margin_pct"],\n  "group_by": "item",\n  "compare": ["this_month", "last_month"],\n  "order": "delta_asc", "limit": 5 }`,
  },
];
export function BrainAsk() {
  const [i, setI] = useState(0);
  const cur = BRAIN_QS[i];
  const max = 100;
  return (
    <div className="tk-board">
      <div className="tk-board__bar"><img src={A.kmarkInk} alt="" /><span>Chef Brain · ask</span><span>Back office</span></div>
      <div className="tk-board__body tk-ask">
        <div className="tk-row" role="group" aria-label="Example questions">
          {BRAIN_QS.map((_b, k) => <button key={k} className="tk-tab" style={{ borderColor: "var(--rule-bone)", ...(k === i ? { background: "var(--ink)", color: "var(--bone)" } : {}) }} aria-pressed={k === i} onClick={() => setI(k)}>Question {k + 1}</button>)}
        </div>
        <div className="tk-ask__q"><Icon name="chat" /><span>{cur.q}</span></div>
        <svg viewBox="0 0 520 132" className="tk-matrix" role="img" aria-label="Comparison bars for the question above. Example data.">
          {cur.bars.map((b, k) => {
            const y = 8 + k * 62;
            return (
              <g key={b.l}>
                <text x="0" y={y + 10} fontSize="12" fill="#5C5349">{b.l}</text>
                <rect x="0" y={y + 18} width={(b.a / max) * 440} height="14" rx="3" fill="#17130F" />
                <text x={(b.a / max) * 440 + 8} y={y + 30} fontSize="12" fill="#17130F">{b.a} · this period</text>
                <rect x="0" y={y + 36} width={(b.b / max) * 440} height="14" rx="3" fill="#C9BFB3" />
                <text x={(b.b / max) * 440 + 8} y={y + 48} fontSize="12" fill="#5C5349">{b.b} · comparison</text>
              </g>
            );
          })}
        </svg>
        <p className="tk-ask__a">{cur.a}</p>
        <details>
          <summary style={{ cursor: "pointer", fontSize: "0.86rem", color: "var(--on-bone-mute)" }}>See the plan the figure was computed from</summary>
          <pre className="tk-plan" style={{ marginTop: 10 }}>{cur.plan}</pre>
        </details>
        <p className="tk-fine"><Basis kind="Illustrative" /> &nbsp;Example data.</p>
      </div>
    </div>
  );
}

/* ====================================================================
   Korner Ask: the assistant on the order-taking tablet and handheld.
   ==================================================================== */
const SEATS = [
  { n: 1, name: "Mrs. John", sub: "Third visit", chips: [{ t: "White Burgundy", gold: true }] },
  { n: 2, name: "Mr. John", sub: "Regular · 9 visits", chips: [{ t: "Shellfish allergy", warn: true }, { t: "Barolo", gold: true }] },
  { n: 3, name: "Ms. Laila", sub: "Guest of the table", chips: [{ t: "Anniversary", gold: true }] },
  { n: 4, name: "Guest", sub: "First visit · ask", chips: [] as { t: string; gold?: boolean; warn?: boolean }[] },
];
const KQ = [
  { seat: 2, q: "What are Mr. John's allergies at Table 22, and what wine does he prefer?",
    allergy: { t: "Shellfish allergy, seat 2", s: "Confirmed again tonight · on every ticket for this seat" },
    a: "He ordered the 2016 Barolo on his last two visits and prefers still water, no ice. The turbot is cleared for him. The bisque amuse-bouche is not: the kitchen has the alternative ready." },
  { seat: 3, q: "Is anyone at Table 22 celebrating?",
    a: "Seat 3, Ms. Laila. The booking notes an anniversary. Pastry has been briefed for a plate with dessert, and the table has not been told." },
  { seat: 1, q: "What should I suggest with the turbot for seat 1?",
    a: "Mrs. John chooses white Burgundy and has leaned mineral over oak on three visits. The Chablis Premier Cru is open by the glass tonight, and the cellar would like it poured." },
];
export function KornerTablet() {
  const [i, setI] = useState(0);
  const cur = KQ[i];
  return (
    <div className="tk-tablet">
      <div className="tk-tablet__screen">
        <div className="tk-tablet__side">
          <div><span className="tk-mono" style={{ color: "var(--gold)" }}>Table 22 · 20:42</span><h4>Party of four</h4></div>
          {SEATS.map((s) => (
            <div key={s.n} className="tk-seat" data-on={s.n === cur.seat}>
              <span className="tk-seat__n">{s.n}</span>
              <div>{s.name}<small>{s.sub}</small>
                {s.chips.length > 0 && <span className="tk-chips">{s.chips.map((c) => <span key={c.t} className={"tk-chip " + (c.warn ? "tk-chip--warn" : "tk-chip--gold")}>{c.t}</span>)}</span>}
              </div>
            </div>
          ))}
        </div>
        <div className="tk-tablet__main">
          <span className="tk-mono" style={{ color: "var(--on-ink-mute)" }}>Korner Ask</span>
          <div className="tk-kq" role="group" aria-label="Ask about this table">
            {KQ.map((k, n) => <button key={n} aria-pressed={n === i} onClick={() => setI(n)}>{k.q}</button>)}
          </div>
          <div className="tk-kmsg tk-kmsg--q">{cur.q}</div>
          {cur.allergy && <div className="tk-allergy"><Icon name="alert" /><div>{cur.allergy.t}<small>{cur.allergy.s}</small></div></div>}
          <div className="tk-kmsg tk-kmsg--a">{cur.a}</div>
        </div>
      </div>
    </div>
  );
}
